import {
  BarChart3,
  BookMarked,
  BookOpen,
  Building2,
  Calendar,
  CalendarDays,
  Church,
  ChevronDown,
  CreditCard,
  Minus,
  Plus,
  ClipboardCheck,
  Contact,
  ClipboardList,
  FileBarChart2,
  FileText,
  GitBranch,
  HandCoins,
  HandHeart,
  HeartHandshake,
  History,
  Landmark,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  Mail,
  Megaphone,
  Menu,
  MessagesSquare,
  Package,
  PieChart,
  PiggyBank,
  Receipt,
  Scale,
  ScrollText,
  LifeBuoy,
  Settings,
  Smartphone,
  Target,
  Users,
  UsersRound,
  UserStar,
  Wallet,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router';

import { BranchSwitcher } from '@/components/layout/branch-switcher';
import { SupportAccessBanner } from '@/components/layout/support-access-banner';
import { SupportSessionBanner } from '@/components/layout/support-session-banner';
import { NotificationCenter } from '@/components/layout/notification-center';
import { UserMenu } from '@/components/layout/user-menu';
import { CommandPalette } from '@/components/shared/command-palette';
import { CreateActionMenu } from '@/components/shared/create-action-menu';
import { SetupChecklist } from '@/components/shared/setup-checklist';
import { UnityLogo } from '@/components/shared/unity-logo';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { shouldShowChurchSetup } from '@/lib/church-setup';
import { canAccessNavPath, canAccessSettings } from '@/lib/module-access';
import { useChurchBrand } from '@/hooks/use-church-brand';
import { useConsoleNotificationRealtime } from '@/hooks/use-console-notifications';
import { useAuthStore } from '@/stores/auth-store';
import { useChurchStore } from '@/stores/church-store';
import { cn } from '@/utils/cn';

/**
 * Console chrome: collapsible sidebar groups with icons, top bar, account menu.
 *
 * Each category toggles independently. A plus on the Overview header expands every
 * section, or collapses them when they are already open. The Overview chevron
 * collapses every section while any section is open, and opens them all when
 * none are. The active route's group still opens on navigation.
 */

interface NavItem {
  code: string;
  to: string;
  labelKey: string;
  icon: LucideIcon;
  /** Override default prefix matching when sibling routes share a path prefix. */
  isActive?: (pathname: string) => boolean;
  /** Always shown when permitted — not gated by an enabled church module (e.g. Support). */
  alwaysAvailable?: boolean;
}

type NavSectionAccentClass =
  | 'text-nav-section-overview'
  | 'text-nav-section-people'
  | 'text-nav-section-ministry'
  | 'text-nav-section-finance'
  | 'text-nav-section-insights'
  | 'text-nav-section-administration'
  | 'text-nav-section-organisation';

interface NavGroup {
  id: string;
  labelKey: string;
  icon: LucideIcon;
  /** Heading colour on the navy sidebar. A token utility, never a raw colour. */
  accentClass: NavSectionAccentClass;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    id: 'overview',
    labelKey: 'navGroup.overview',
    icon: LayoutGrid,
    accentClass: 'text-nav-section-overview',
    items: [
      { code: 'reporting', to: '/home', labelKey: 'nav.dashboard', icon: LayoutDashboard },
    ],
  },
  {
    id: 'people',
    labelKey: 'navGroup.people',
    icon: Users,
    accentClass: 'text-nav-section-people',
    items: [
      {
        code: 'people',
        to: '/people',
        labelKey: 'nav.people',
        icon: Contact,
        isActive: (pathname) =>
          pathname === '/people' ||
          pathname.startsWith('/people/families') ||
          pathname.startsWith('/people/follow-ups') ||
          pathname.startsWith('/people/join-requests') ||
          (/^\/people\/[^/]+$/.test(pathname) &&
            !pathname.startsWith('/people/leadership') &&
            !pathname.startsWith('/people/departments')),
      },
      {
        code: 'people',
        to: '/people/leadership',
        labelKey: 'nav.leadership',
        icon: UserStar,
        isActive: (pathname) => pathname.startsWith('/people/leadership'),
      },
      {
        code: 'people',
        to: '/people/departments',
        labelKey: 'nav.departments',
        icon: Layers,
        isActive: (pathname) => pathname.startsWith('/people/departments'),
      },
      { code: 'groups', to: '/committees', labelKey: 'nav.committees', icon: Landmark, isActive: (pathname) => pathname.startsWith('/committees') },
      {
        code: 'groups',
        to: '/groups',
        labelKey: 'nav.groups',
        icon: UsersRound,
        isActive: (pathname) =>
          pathname === '/groups' ||
          pathname.startsWith('/groups/auto-filled') ||
          (/^\/groups\/[^/]+$/.test(pathname) && pathname !== '/groups/auto-filled'),
      },
      {
        code: 'sacraments',
        to: '/sacraments',
        labelKey: 'nav.sacraments',
        icon: BookMarked,
        isActive: (pathname) => pathname.startsWith('/sacraments'),
      },
    ],
  },
  {
    id: 'ministry',
    labelKey: 'navGroup.ministry',
    icon: HeartHandshake,
    accentClass: 'text-nav-section-ministry',
    items: [
      { code: 'programs', to: '/calendar', labelKey: 'nav.calendar', icon: Calendar },
      {
        code: 'programs',
        to: '/services',
        labelKey: 'nav.services',
        icon: Church,
        isActive: (pathname) =>
          pathname === '/services' || pathname.startsWith('/services/'),
      },
      { code: 'programs', to: '/programs', labelKey: 'nav.programs', icon: CalendarDays },
      { code: 'attendance', to: '/attendance', labelKey: 'nav.attendance', icon: ClipboardCheck },
      { code: 'content', to: '/content', labelKey: 'nav.content', icon: BookOpen },
      { code: 'prayer', to: '/prayer', labelKey: 'nav.prayer', icon: HandHeart },
      {
        code: 'pastoral',
        to: '/pastoral',
        labelKey: 'nav.pastoral',
        icon: HeartHandshake,
        isActive: (pathname) => pathname.startsWith('/pastoral'),
      },
      { code: 'evangelism', to: '/evangelism', labelKey: 'nav.evangelism', icon: Megaphone },
      { code: 'community', to: '/community', labelKey: 'nav.community', icon: MessagesSquare },
      {
        code: 'documents',
        to: '/documents',
        labelKey: 'nav.documents',
        icon: FileText,
        isActive: (pathname) => pathname.startsWith('/documents'),
      },
      {
        code: 'content',
        to: '/chronicle',
        labelKey: 'nav.chronicle',
        icon: ScrollText,
        isActive: (pathname) =>
          pathname === '/chronicle' || pathname.startsWith('/chronicle/'),
      },
      { code: 'messaging', to: '/messaging', labelKey: 'nav.messaging', icon: Mail },
    ],
  },
  {
    id: 'finance',
    labelKey: 'navGroup.finance',
    icon: Wallet,
    accentClass: 'text-nav-section-finance',
    items: [
      {
        code: 'giving',
        to: '/giving',
        labelKey: 'nav.giving',
        icon: HandCoins,
        isActive: (pathname) =>
          pathname === '/giving' ||
          (pathname.startsWith('/giving/') && !pathname.startsWith('/giving/funds')),
      },
      {
        code: 'giving',
        to: '/giving/funds',
        labelKey: 'nav.funds',
        icon: PiggyBank,
        isActive: (pathname) =>
          pathname === '/giving/funds' || pathname.startsWith('/giving/funds/'),
      },
      { code: 'projects', to: '/projects', labelKey: 'nav.pledges', icon: Target },
      { code: 'spending', to: '/expenses', labelKey: 'nav.expenses', icon: Receipt },
      { code: 'welfare', to: '/welfare', labelKey: 'nav.welfare', icon: HeartHandshake },
      { code: 'budgets', to: '/budgets', labelKey: 'nav.budgets', icon: PieChart },
      {
        code: 'spending',
        to: '/financial-position',
        labelKey: 'nav.financialPosition',
        icon: Scale,
      },
      { code: 'payments', to: '/payments', labelKey: 'nav.payments', icon: Smartphone },
    ],
  },
  {
    id: 'insights',
    labelKey: 'navGroup.insights',
    icon: BarChart3,
    accentClass: 'text-nav-section-insights',
    items: [
      { code: 'reporting', to: '/reports', labelKey: 'nav.reports', icon: FileBarChart2 },
    ],
  },
  {
    id: 'administration',
    labelKey: 'navGroup.administration',
    icon: ClipboardList,
    accentClass: 'text-nav-section-administration',
    items: [
      {
        code: 'audit',
        to: '/audit',
        labelKey: 'nav.audit',
        icon: History,
        isActive: (pathname) => pathname === '/audit',
      },
      {
        code: 'audit',
        to: '/audit/financial',
        labelKey: 'nav.financialAudit',
        icon: Landmark,
        isActive: (pathname) => pathname.startsWith('/audit/financial'),
      },
      {
        code: 'support',
        to: '/support/tickets',
        labelKey: 'nav.support',
        icon: LifeBuoy,
        alwaysAvailable: true,
        isActive: (pathname) => pathname.startsWith('/support'),
      },
    ],
  },
  {
    id: 'organisation',
    labelKey: 'navGroup.organisation',
    icon: Building2,
    accentClass: 'text-nav-section-organisation',
    items: [
      { code: 'branches', to: '/branches', labelKey: 'nav.branches', icon: GitBranch },
      {
        code: 'assets',
        to: '/assets',
        labelKey: 'nav.assets',
        icon: Package,
        isActive: (pathname) => pathname.startsWith('/assets'),
      },
    ],
  },
];

/** Modules whose console pages do not ship yet — keep them out of the sidebar. */
const NAV_READY = new Set([
  'reporting',
  'people',
  'groups',
  'programs',
  'attendance',
  'giving',
  'projects',
  'spending',
  'welfare',
  'budgets',
  'payments',
  'messaging',
  'content',
  'prayer',
  'pastoral',
  'evangelism',
  'community',
  'documents',
  'sacraments',
  'assets',
  'branches',
  'audit',
  'support',
]);

const NAV_SECTION_KEY = 'trinity.nav.sections';

const pathMatchesItem = (pathname: string, to: string) => {
  return pathname === to || pathname.startsWith(`${to}/`);
};

const readStoredSections = (): Record<string, boolean> => {
  try {
    const raw = localStorage.getItem(NAV_SECTION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
};

const isGroupExpanded = (groupId: string, expanded: Record<string, boolean>) =>
  expanded[groupId] === true;

const persistExpanded = (next: Record<string, boolean>) => {
  try {
    localStorage.setItem(NAV_SECTION_KEY, JSON.stringify(next));
  } catch {
    // Ignore quota / private-mode failures.
  }
  return next;
};

export const toggleNavGroupExpanded = (
  groupId: string,
  prev: Record<string, boolean>,
): Record<string, boolean> => ({
  ...prev,
  [groupId]: !isGroupExpanded(groupId, prev),
});

export const expandAllNavGroups = (groupIds: string[]): Record<string, boolean> =>
  Object.fromEntries(groupIds.map((id) => [id, true]));

export const collapseAllNavGroups = (groupIds: string[]): Record<string, boolean> =>
  Object.fromEntries(groupIds.map((id) => [id, false]));

export const areAllNavGroupsExpanded = (
  groupIds: string[],
  expanded: Record<string, boolean>,
): boolean => groupIds.every((id) => isGroupExpanded(id, expanded));

export const anyNavGroupExpanded = (
  groupIds: string[],
  expanded: Record<string, boolean>,
): boolean => groupIds.some((id) => isGroupExpanded(id, expanded));

/** Overview chevron: collapse every section while any is open, otherwise open them all. */
export const toggleAllFromOverviewDropdown = (
  groupIds: string[],
  expanded: Record<string, boolean>,
): Record<string, boolean> =>
  anyNavGroupExpanded(groupIds, expanded)
    ? collapseAllNavGroups(groupIds)
    : expandAllNavGroups(groupIds);

type ResolvedNavGroup = NavGroup & { items: NavItem[] };

const SidebarPanel = ({
  groups,
  expanded,
  onToggleGroup,
  onToggleAllSections,
  onOverviewDropdown,
  pathname,
  settingsActive,
  subscriptionsActive,
  showSettings,
  showSubscriptions,
  onNavigate,
  showCloseButton,
  onClose,
}: {
  groups: ResolvedNavGroup[];
  expanded: Record<string, boolean>;
  onToggleGroup: (groupId: string) => void;
  onToggleAllSections: () => void;
  onOverviewDropdown: () => void;
  pathname: string;
  settingsActive: boolean;
  subscriptionsActive: boolean;
  showSettings: boolean;
  showSubscriptions: boolean;
  onNavigate?: () => void;
  showCloseButton?: boolean;
  onClose?: () => void;
}) => {
  const { t: tCommon } = useTranslation();
  const showSectionControls = groups.length > 1;
  const anyExpanded = anyNavGroupExpanded(
    groups.map((group) => group.id),
    expanded,
  );
  const allExpanded = areAllNavGroupsExpanded(
    groups.map((group) => group.id),
    expanded,
  );

  return (
    <>
      <div className="flex min-h-14 shrink-0 items-center gap-2.5 border-b border-nav-border px-4 py-2">
        <UnityLogo variant="symbol-on-dark" className="size-8 shrink-0" />
        <p className="min-w-0 flex-1 text-body font-semibold leading-snug text-nav-fg">
          {tCommon('app.name')}{' '}
          <span className="font-medium text-nav-fg-muted">{tCommon('app.descriptor')}</span>
        </p>
        {showCloseButton ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0"
            aria-label={tCommon('nav.closeMenu')}
            onClick={onClose}
          >
            <X className="size-5" aria-hidden />
          </Button>
        ) : null}
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <nav className="flex flex-col gap-1 p-3" aria-label={tCommon('app.name')}>
          {groups.map((group) => {
            const GroupIcon = group.icon;
            const open = isGroupExpanded(group.id, expanded);
            const panelId = `nav-section-${group.id}`;
            const triggerId = `${panelId}-trigger`;
            const showAllSectionsControl = showSectionControls && group.id === 'overview';

            return (
              <div key={group.id} className="rounded-md">
                {showAllSectionsControl ? (
                  <div className="flex w-full items-center rounded-md">
                    <button
                      type="button"
                      id={triggerId}
                      aria-expanded={open}
                      aria-controls={panelId}
                      className={cn(
                        'flex min-w-0 flex-1 items-center gap-2 rounded-l-md px-2.5 py-2 text-left transition-colors',
                        'hover:bg-nav-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-active-fg/40',
                        group.accentClass,
                      )}
                      onClick={() => onToggleGroup(group.id)}
                    >
                      <GroupIcon className="size-4 shrink-0" aria-hidden />
                      <span className="min-w-0 flex-1 truncate text-overline font-semibold tracking-wide uppercase">
                        {tCommon(group.labelKey)}
                      </span>
                    </button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-7 shrink-0 rounded-none text-nav-fg-subtle hover:bg-nav-hover"
                      aria-label={
                        allExpanded ? tCommon('nav.collapseAll') : tCommon('nav.openAll')
                      }
                      onClick={onToggleAllSections}
                    >
                      {allExpanded ? (
                        <Minus className="size-3.5 shrink-0" aria-hidden />
                      ) : (
                        <Plus className="size-3.5 shrink-0" aria-hidden />
                      )}
                    </Button>
                    <button
                      type="button"
                      aria-expanded={anyExpanded}
                      aria-label={
                        anyExpanded ? tCommon('nav.collapseAll') : tCommon('nav.openAll')
                      }
                      className={cn(
                        'flex shrink-0 items-center rounded-r-md px-2.5 py-2 text-nav-fg-subtle transition-colors',
                        'hover:bg-nav-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-active-fg/40',
                      )}
                      onClick={onOverviewDropdown}
                    >
                      <ChevronDown
                        aria-hidden
                        className={cn(
                          'size-3.5 shrink-0 transition-transform duration-200',
                          anyExpanded && 'rotate-180',
                        )}
                      />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    id={triggerId}
                    aria-expanded={open}
                    aria-controls={panelId}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left transition-colors',
                      'hover:bg-nav-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-active-fg/40',
                      group.accentClass,
                    )}
                    onClick={() => onToggleGroup(group.id)}
                  >
                    <GroupIcon className="size-4 shrink-0" aria-hidden />
                    <span className="min-w-0 flex-1 truncate text-overline font-semibold tracking-wide uppercase">
                      {tCommon(group.labelKey)}
                    </span>
                    <ChevronDown
                      aria-hidden
                      className={cn(
                        'size-3.5 shrink-0 text-nav-fg-subtle transition-transform duration-200',
                        open && 'rotate-180',
                      )}
                    />
                  </button>
                )}

                {open ? (
                  <section
                    id={panelId}
                    aria-labelledby={triggerId}
                    className="mt-0.5 flex flex-col gap-0.5 pb-2 pl-1"
                  >
                    {group.items.map((item) => {
                      const ItemIcon = item.icon;
                      const hasDeeperSibling = group.items.some(
                        (other) =>
                          other.to !== item.to && other.to.startsWith(`${item.to}/`),
                      );
                      const forceActive = item.isActive?.(pathname);
                      return (
                        <ShellLink
                          key={`${item.code}-${item.to}`}
                          to={item.to}
                          end={item.to === '/' || hasDeeperSibling || Boolean(item.isActive)}
                          forceActive={forceActive}
                          onNavigate={onNavigate}
                        >
                          <ItemIcon className="size-4 shrink-0" aria-hidden />
                          {tCommon(item.labelKey)}
                        </ShellLink>
                      );
                    })}
                  </section>
                ) : null}
              </div>
            );
          })}

          {showSubscriptions || showSettings ? (
            <div className="mt-1 flex flex-col gap-0.5 border-t border-nav-border pt-3">
              {showSubscriptions ? (
                <ShellLink
                  to="/subscriptions"
                  forceActive={subscriptionsActive}
                  onNavigate={onNavigate}
                >
                  <CreditCard className="size-4 shrink-0" aria-hidden />
                  {tCommon('nav.subscriptions')}
                </ShellLink>
              ) : null}
              {showSettings ? (
                <ShellLink to="/settings" forceActive={settingsActive} onNavigate={onNavigate}>
                  <Settings className="size-4 shrink-0" aria-hidden />
                  {tCommon('nav.settings')}
                </ShellLink>
              ) : null}
            </div>
          ) : null}
        </nav>
      </ScrollArea>
    </>
  );
};

export const AppShell = () => {
  useChurchBrand();
  useConsoleNotificationRealtime();
  const { t: tCommon } = useTranslation();
  const location = useLocation();
  // Subscribe to the list itself — selecting `isModuleEnabled` alone never re-renders
  // when modules finish loading, which leaves the sidebar empty except Settings.
  const enabledModules = useChurchStore((state) => state.enabledModules);
  const church = useChurchStore((state) => state.church);
  const workspaceGeneration = useChurchStore((state) => state.workspaceGeneration);
  const membership = useChurchStore((state) => state.membership);
  const enabledCodes = useMemo(
    () => new Set(enabledModules.map((module) => module.code)),
    [enabledModules],
  );
  const isModuleEnabled = useChurchStore((state) => state.isModuleEnabled);

  const supportVisit = Boolean(useAuthStore((state) => state.user?.supportAccess));
  const showSetupGuide =
    church != null &&
    !supportVisit &&
    (membership?.systemRole === 'owner' || membership?.systemRole === 'admin') &&
    shouldShowChurchSetup(church.id);

  const groups = useMemo(
    () =>
      NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          if (!NAV_READY.has(item.code)) return false;
          if (!item.alwaysAvailable && !enabledCodes.has(item.code)) return false;
          return canAccessNavPath(
            membership,
            item.code,
            item.to,
            item.alwaysAvailable ? () => true : isModuleEnabled,
          );
        }),
      })).filter((group) => group.items.length > 0),
    [enabledCodes, isModuleEnabled, membership],
  );

  const isCongregant = membership?.systemRole === 'congregant';
  const showSettings = !isCongregant && canAccessSettings(membership);
  const showSubscriptions = canAccessSettings(membership);

  const activeGroupId = useMemo(
    () =>
      groups.find((group) =>
        group.items.some((item) =>
          item.isActive
            ? item.isActive(location.pathname)
            : pathMatchesItem(location.pathname, item.to),
        ),
      )?.id ?? null,
    [groups, location.pathname],
  );

  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => readStoredSections());
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const groupIds = useMemo(() => groups.map((group) => group.id), [groups]);

  // Keep the active group open when navigating, without forcing closed groups the user
  // already chose to leave open.
  useEffect(() => {
    if (!activeGroupId) return;
    setExpanded((prev) => {
      if (prev[activeGroupId] === true) return prev;
      return persistExpanded({ ...prev, [activeGroupId]: true });
    });
  }, [activeGroupId]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileNavOpen]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileNavOpen]);

  const onToggleGroup = (groupId: string) => {
    setExpanded((prev) => persistExpanded(toggleNavGroupExpanded(groupId, prev)));
  };

  const onToggleAllSections = () => {
    setExpanded((prev) => {
      const allExpanded = areAllNavGroupsExpanded(groupIds, prev);
      return persistExpanded(
        allExpanded ? collapseAllNavGroups(groupIds) : expandAllNavGroups(groupIds),
      );
    });
  };

  const onOverviewDropdown = () => {
    setExpanded((prev) => persistExpanded(toggleAllFromOverviewDropdown(groupIds, prev)));
  };

  const settingsActive =
    location.pathname === '/settings' || location.pathname.startsWith('/settings/');
  const subscriptionsActive =
    location.pathname === '/subscriptions' || location.pathname.startsWith('/subscriptions/');

  const closeMobileNav = () => setMobileNavOpen(false);

  const sidebarProps = {
    groups,
    expanded,
    onToggleGroup,
    onToggleAllSections,
    onOverviewDropdown,
    pathname: location.pathname,
    settingsActive,
    subscriptionsActive,
    showSettings,
    showSubscriptions,
  };

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-background text-fg">
      <SupportAccessBanner />
      <SupportSessionBanner />
      <div className="flex min-h-0 flex-1 overflow-hidden">
      <aside className="hidden h-full w-60 shrink-0 flex-col border-r border-nav-border bg-nav text-nav-fg md:flex">
        <SidebarPanel {...sidebarProps} />
      </aside>

      {mobileNavOpen
        ? createPortal(
            <div className="fixed inset-0 z-[100] md:hidden">
              <button
                type="button"
                className="absolute inset-0 bg-overlay backdrop-blur-[2px]"
                aria-label={tCommon('nav.closeMenu')}
                onClick={closeMobileNav}
              />
              <aside className="relative flex h-full w-[min(100%,18rem)] flex-col border-r border-nav-border bg-nav text-nav-fg shadow-e3">
                <SidebarPanel
                  {...sidebarProps}
                  showCloseButton
                  onClose={closeMobileNav}
                  onNavigate={closeMobileNav}
                />
              </aside>
            </div>,
            document.body,
          )
        : null}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-border bg-surface px-3 sm:gap-3 sm:px-6">
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 md:hidden"
              aria-label={tCommon('nav.openMenu')}
              aria-expanded={mobileNavOpen}
              onClick={() => setMobileNavOpen(true)}
            >
              <Menu className="size-5" aria-hidden />
            </Button>
            <BranchSwitcher />
          </div>
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            {!isCongregant ? (
              <>
                <CommandPalette />
                {supportVisit ? null : (
                  <>
                    <CreateActionMenu align="end" className="hidden sm:inline-flex" />
                    <CreateActionMenu compact align="end" className="shrink-0 sm:hidden" />
                  </>
                )}
                <NotificationCenter />
              </>
            ) : null}
            <UserMenu />
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[var(--content-max-width)] px-3 py-4 sm:px-6 sm:py-6">
            <Outlet key={church ? `${church.id}:${workspaceGeneration}` : 'none'} />
          </div>
        </main>
      </div>

      </div>
      {showSetupGuide && church ? (
        <SetupChecklist churchId={church.id} churchName={church.name} />
      ) : null}
    </div>
  );
};

const ShellLink = ({
  to,
  end,
  children,
  forceActive,
  onNavigate,
}: {
  to: string;
  end?: boolean;
  children: ReactNode;
  forceActive?: boolean;
  onNavigate?: () => void;
}) => (
  <NavLink
    to={to}
    end={end}
    onClick={onNavigate}
    className={({ isActive }) =>
      cn(
        'flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-body-sm transition-colors',
        forceActive || isActive
          ? 'bg-nav-active font-medium text-nav-active-fg'
          : 'text-nav-fg-muted hover:bg-nav-hover hover:text-nav-fg',
      )
    }
  >
    {children}
  </NavLink>
);
